import { InfoIcon } from 'lucide-react'

import { Button, ButtonGroup, Checkbox, Tooltip, useBreakpoint } from 'slab'
import { formatMoney } from 'utils'

import { type MedicalPlan, type MedicalPlanDisplayItem } from '../medicalPlanTypes'
import { useOnboarding } from '../OnboardingContext'
import { type OnboardingEnrollment, type PlanCardBadgeCategory } from '../types'
import {
  calculateRemainingBudgetForTieredAllowance,
  extractInNetworkAmount,
  extractInNetworkString,
  getDeductibleTooltipLabel,
  getRecommendationReasonLabels,
  MOOP_INFO,
  PRIMARY_DOCTOR_VISITS_INFO,
  SPECIALIST_DOCTOR_VISITS_INFO,
} from '../utils'
import {
  getRemainingAllowanceWithEnrollments,
  SelectPlanButton,
} from './MemberOnboardingPlanCard'
import { OnboardingMetalTierBadge } from './OnboardingMetalTierBadge/OnboardingMetalTierBadge'
import { PlanBadge } from './PlanBadge/PlanBadge'
import { PlanCard } from './PlanCard'
import { PlanChip } from './PlanChip'
import { ProviderInNetworkMap } from './ProviderInNetworkMap'
import { ProviderInNetworkVerification } from './ProviderInNetworkVerification'

export type MedicalPlanProps = {
  badgeCategories?: PlanCardBadgeCategory[];
  currentPlanEnrollment?: OnboardingEnrollment | null;
  planDisplayItem: MedicalPlanDisplayItem;
  onSelectPlan?: (plan: MedicalPlan) => void;
  setShowMedicalPlanDetail: (plan: MedicalPlanDisplayItem) => void;
  selectedPlansForComparison?: MedicalPlanDisplayItem[];
  verifyingInNetworkStatusPlanIds?: string[];
  onPlanComparisonToggle?: (
    plan: MedicalPlanDisplayItem,
    isSelected: boolean
  ) => void;
  onPlanVerification?: (plan: MedicalPlanDisplayItem) => void;
  selectedPlansForVerification?: MedicalPlanDisplayItem[],
  onTogglePlanVerification?: (plan: MedicalPlanDisplayItem, isSelected: boolean) => void;
}

export function MedicalPlanCard ({
  badgeCategories,
  currentPlanEnrollment,
  onSelectPlan,
  planDisplayItem,
  setShowMedicalPlanDetail,
  selectedPlansForComparison,
  selectedPlansForVerification,
  verifyingInNetworkStatusPlanIds,
  onPlanComparisonToggle,
  onPlanVerification,
  onTogglePlanVerification,
}: MedicalPlanProps) {
  const { onboardingData, isFerryVerificationTasksEnabled, qmeEnabledForHsaEligiblePlans } = useOnboarding()
  const { breakpointIsAtLeast } = useBreakpoint()

  const plan = planDisplayItem.plan

  const premium = Number(
    ((planDisplayItem.total_premium.subunits || 0) / 100).toFixed(2)
  )

  const isMemberOnTieredAllowance = !!planDisplayItem.tiered_allowance_employee_only_premium
  let difference = null
  if (isMemberOnTieredAllowance) {
    difference = calculateRemainingBudgetForTieredAllowance({
      planDisplayItem,
    })
  } else if (onboardingData) {
    difference = getRemainingAllowanceWithEnrollments(
      onboardingData,
      premium,
      'medical'
    )
  }

  const isIndividual =
    (onboardingData?.additional_participants || []).length === 0

  const isSelected =
    currentPlanEnrollment &&
    currentPlanEnrollment.carrier_name?.toLowerCase() ===
      plan.carrier_name.toLowerCase() &&
    currentPlanEnrollment.plan_code?.toLowerCase() === plan.id

  const isComparisonSelected = selectedPlansForComparison?.some(
    (p) => p.plan.id === plan.id
  )
  const isVerificationSelected = selectedPlansForVerification?.some(
    (p) => p.plan.id === plan.id
  )

  const parsedDeductible = extractInNetworkAmount(
    isIndividual
      ? plan.individual_medical_deductible
      : plan.family_medical_deductible
  )
  const deductibleValue =
    typeof parsedDeductible === 'string'
      ? parsedDeductible
      : formatMoney(parsedDeductible)

  const parsedMOOP = extractInNetworkAmount(
    isIndividual ? plan.individual_medical_moop : plan.family_medical_moop
  )

  const providerInNetworkMap = planDisplayItem.provider_in_network_map
  const inNetworkProvidersCount = planDisplayItem.in_network_providers_count
  const inNetworkVerificationProgressStatus = planDisplayItem.in_network_verification_progress_status

  const isUserQMEEligible =
    onboardingData?.allowance?.qme_treatment === 'all_allowed'

  const labels: { cardLabel: string; tooltipLabel: string, tableLabel: string }[] = []
  if (badgeCategories && badgeCategories.length > 0) {
    badgeCategories.forEach((category) => {
      const label = getRecommendationReasonLabels(category)
      labels.push(label)
    })
  }

  const premiumComponent = difference !== null && (
    <PlanCard.Premium
      align={breakpointIsAtLeast('xl') ? 'right' : 'left'}
      amount={premium}
      remainder={difference}
      isTieredAllowance={isMemberOnTieredAllowance}
    />
  )

  return (
    <>
      {badgeCategories && badgeCategories.length > 0 && (
        <div className='flex'>
          {labels.map((label) => (
            <div className='text-left' key={label.cardLabel}>
              <Tooltip content={label.tooltipLabel}>
                <div className='ml-2 px-4 py-2 rounded-t-lg inline-block bg-violet-50 text-violet-500 body-sm-bold'>
                  {label.cardLabel}
                </div>
              </Tooltip>
            </div>
          ))}
        </div>
      )}
      <PlanCard
        layout={breakpointIsAtLeast('xs') ? 'horizontal' : 'vertical'}
        selected={!!isSelected || (badgeCategories?.includes('renewal') && !currentPlanEnrollment)}
        footer={
          onTogglePlanVerification && (
            <Checkbox
              checked={isVerificationSelected}
              onChange={(e) => onTogglePlanVerification(planDisplayItem, e.target.checked)}
              label='Add this plan to the call list'
            />
          )
        }
      >
        <PlanCard.GraySection wideMode>
          <div className='flex flex-col gap-4 h-full'>
            <PlanChip
              carrierLogo={plan.carrier_image_url ?? ''}
              carrierName={plan.carrier_name}
              planName={plan.friendly_name}
            />
            <div className='flex gap-2'>
              <OnboardingMetalTierBadge bordered tier={plan.metal_tier} />
              <PlanBadge bordered type={plan.plan_type} />
            </div>
            {!qmeEnabledForHsaEligiblePlans && isUserQMEEligible && plan.hsa_eligible && (
              <div className='flex items-center text-gray-800 caption gap-x-1'>
                Turns off Thatch balance
                <Tooltip
                  content={
                    "You can't contribute to an HSA and carry a Thatch balance at the same time."
                  }
                >
                  <InfoIcon aria-label='Info icon' size={10} />
                </Tooltip>
              </div>
            )}
            {providerInNetworkMap && inNetworkProvidersCount !== undefined && !isFerryVerificationTasksEnabled && (
              <ProviderInNetworkMap
                providerInNetworkMap={providerInNetworkMap}
                inNetworkProvidersCount={inNetworkProvidersCount}
                providerSupportLink={plan.provider_support_link}
              />
            )}
            {providerInNetworkMap && inNetworkProvidersCount !== undefined && isFerryVerificationTasksEnabled && (
              <>
                <ProviderInNetworkVerification
                  providerInNetworkMap={providerInNetworkMap}
                  inNetworkProvidersCount={inNetworkProvidersCount}
                  verifyingInNetworkStatusPlanIds={verifyingInNetworkStatusPlanIds}
                  inNetworkVerificationProgressStatus={inNetworkVerificationProgressStatus}
                  providerSupportLink={plan.provider_support_link}
                  onPlanVerification={onPlanVerification ? () => onPlanVerification(planDisplayItem) : undefined}
                />
              </>
            )}
            {
              onPlanComparisonToggle && selectedPlansForComparison && (
                <div className='mt-auto'>
                  <Checkbox
                    label='Compare plan'
                    checked={isComparisonSelected}
                    onChange={(e) =>
                      onPlanComparisonToggle(planDisplayItem, e.target.checked)}
                    disabled={
                      !isComparisonSelected &&
                      selectedPlansForComparison.length >= 3
                    }
                  />
                </div>
              )
            }
          </div>
        </PlanCard.GraySection>
        <PlanCard.Section>
          <div className='flex flex-col gap-6 h-full'>
            <div className='flex flex-col xl:flex-row gap-6 h-full justify-between'>
              {!breakpointIsAtLeast('xl') && premiumComponent}
              <PlanCard.Features>
                <PlanCard.Feature
                  detail={deductibleValue}
                  term={isIndividual ? 'Deductible' : 'Family deductible'}
                  info={getDeductibleTooltipLabel(
                    parsedDeductible,
                    isIndividual
                  )}
                />
                <PlanCard.Feature
                  detail={
                    typeof parsedMOOP === 'string'
                      ? parsedMOOP
                      : formatMoney(parsedMOOP)
                  }
                  term={
                    isIndividual
                      ? 'Out-of-pocket max'
                      : 'Family out-of-pocket max'
                  }
                  info={MOOP_INFO}
                />
                <PlanCard.Feature
                  detail={extractInNetworkString(
                    plan.primary_care_physician_benefit
                  )}
                  term='Primary doctor visits'
                  info={PRIMARY_DOCTOR_VISITS_INFO}
                />
                <PlanCard.Feature
                  detail={extractInNetworkString(plan.specialist_benefit)}
                  term='Specialist doctor visits'
                  info={SPECIALIST_DOCTOR_VISITS_INFO}
                />
              </PlanCard.Features>
              {breakpointIsAtLeast('xl') && premiumComponent}
            </div>
            <ButtonGroup align='right' size='small'>
              <Button
                variant='tertiary-deemphasized'
                onClick={() => setShowMedicalPlanDetail(planDisplayItem)}
              >
                View details
              </Button>
              {
                onSelectPlan && (
                  <SelectPlanButton
                    isCurrentPlan={currentPlanEnrollment}
                    isSelected={isSelected}
                    handleSelectPlan={() => onSelectPlan(plan)}
                  />
                )
              }
            </ButtonGroup>
          </div>
        </PlanCard.Section>
      </PlanCard>
    </>
  )
}
